package com.example.mystybag

import com.arkivanov.decompose.DefaultComponentContext
import com.arkivanov.essenty.lifecycle.LifecycleRegistry
import com.example.mystybag.appCore.helpers.ReLoginHelper
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel
import com.example.mystybag.appCore.views.FeatureViewMystyBag
import com.example.mystybag.common.helpers.CommunicationProtocolImpl
import com.example.mystybag.database.communication.db.DatabaseDriverFactory
import com.example.mystybag.database.communication.db.DatabaseProvider
import com.example.mystybag.common.ui.theme.AppThemeView
import com.example.mystybag.communication.Communication
import com.example.mystybag.core.expected.LocaleHelper
import com.example.mystybag.core.expected.getLanguage
import com.example.mystybag.core.expected.initSharedStringProvider
import com.example.mystybag.core.expected.sharedActivity
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.commonData.expected.isInRelease
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeout
import org.w3c.dom.HTMLElement
import kotlin.js.undefined

/**
 * Suppress "Uncaught (in promise) AbortError: BodyStreamBuffer was aborted"
 * noise originating from Ktor's JS engine when Coil cancels in-flight image
 * requests (component leaves composition, size constraints change, list
 * scroll, the Static-Maps URL changes when the merchant location picker
 * re-centers / re-zooms, etc.).
 *
 * Why this is safe to swallow:
 *   - Coil cancels image jobs as part of normal lifecycle. That cancellation
 *     propagates to Ktor, which calls `AbortController.abort()` on the
 *     underlying browser `fetch()`. The browser then rejects the response
 *     body stream with a `DOMException("BodyStreamBuffer was aborted",
 *     "AbortError")`.
 *   - At that point the calling Ktor coroutine is already cancelled, so the
 *     rejection has no real consumer; it bubbles up to the global
 *     `unhandledrejection` event and webpack-dev-server's overlay flags it
 *     as a runtime error even though it is part of normal cancellation flow.
 *   - There is nothing the app could do with this exception other than
 *     ignore it. We only ignore rejections whose `name` is "AbortError" or
 *     whose message mentions "aborted" — every other rejection still
 *     surfaces to the console / overlay so real bugs are not hidden.
 *
 * We listen on both `unhandledrejection` (for the promise rejection) AND
 * `error` (for the synchronous error event some browsers/devtools dispatch
 * in addition), with `useCapture = true` so we run before the dev-server's
 * own listeners and can `stopImmediatePropagation()` on them.
 */
private fun installAbortErrorSuppression() {
    fun isAbortNoise(name: String, message: String): Boolean =
        name == "AbortError" ||
            message.contains("aborted", ignoreCase = true) ||
            message.contains("BodyStreamBuffer", ignoreCase = true)

    window.addEventListener("unhandledrejection", { event ->
        val pe = event.asDynamic()
        val reason = pe.reason
        if (reason != null) {
            val name = "${reason.name}"
            val message = "${reason.message}"
            if (isAbortNoise(name, message)) {
                pe.preventDefault()
                pe.stopImmediatePropagation()
            }
        }
    }, /* useCapture = */ true)

    window.addEventListener("error", { event ->
        val ee = event.asDynamic()
        // `error` may carry the original Error in `ee.error`, or just message/filename.
        val err = ee.error
        val name = if (err != null) "${err.name}" else ""
        val message = if (err != null) "${err.message}" else "${ee.message}"
        if (isAbortNoise(name, message)) {
            ee.preventDefault()
            ee.stopImmediatePropagation()
        }
    }, /* useCapture = */ true)
}

private fun removeElementById(id: String) {
    val element = document.getElementById(id)
    element?.parentElement?.removeChild(element)
}

/**
 * Enforces Compose-only rendering in the app root: keeps only the Compose canvas under `#root`.
 * Any extra DOM overlays/containers are removed immediately and periodically.
 */
private fun installComposeOnlyDomGuard() {
    fun sanitizeRoot() {
        // Remove known legacy overlay nodes.
        removeElementById("squareDiv")
        val legacyContainer = document.querySelector("container#my-app-container")
        legacyContainer?.parentElement?.removeChild(legacyContainer)

        val root = document.getElementById("root") as? HTMLElement ?: return
        val toRemove = mutableListOf<HTMLElement>()
        val children = root.children
        for (i in 0 until children.length) {
            val child = children.item(i) as? HTMLElement ?: continue
            val isCanvas = child.tagName.lowercase() == "canvas"
            // Allow map containers that are managed by map-specific modules.
            val isAllowedOverlay = child.id.endsWith("-map-container")
            if (!isCanvas && !isAllowedOverlay) {
                toRemove += child
            }
        }
        toRemove.forEach { root.removeChild(it) }
    }

    sanitizeRoot()
    // Keep root clean if any late script injects overlays after startup.
    window.setInterval({ sanitizeRoot() }, 250)
}

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    installAbortErrorSuppression()
    installComposeOnlyDomGuard()
    fun runCompose() {
        try {
            // Web has no Activity; set sharedActivity so PermissionHelper.requestPermission(..., activity = sharedActivity) does not throw
            sharedActivity = Unit
            // DB и схема вече са готови в runComposeAfterStrings() (логове: runSchema done / post-schema check)
            // Required before any API call (e.g. registration/enroll): init config and protocol
            val companyBookApiKey =
                (window.asDynamic().MystyBagCompanyBookApiKey as? String)?.toString()?.trim().orEmpty()
            Communication.init(isInRelease(), CommunicationProtocolImpl, companyBookApiKey)
            Communication.setOnUnauthorized { ReLoginHelper.handleUnauthorized() }
            // Стартираме lifecycle както на iOS – иначе onCreate/onResume на ViewModel-ите не се извикват на Web
            val lifecycle = LifecycleRegistry().apply {
                onCreate()
                onStart()
                onResume()
            }
            val context = AppContext(DefaultComponentContext(lifecycle))
            val viewModel = MystyBagDigitalWalletViewModel(context, null)
            ComposeViewport(viewportContainerId = "root") {
                AppThemeView {
                    FeatureViewMystyBag(viewModel)
                }
            }
        } catch (e: Throwable) {
            val msg = "Startup error: ${e.message}\n\n${e.stackTraceToString()}"
            console.error(msg)
            document.getElementById("root")?.let { root ->
                root.innerHTML = """
                    <pre style="margin:1em;padding:1em;background:#fee;border:1px solid #c00;font-family:monospace;white-space:pre-wrap;word-break:break-all;">${msg.replace("<", "&lt;").replace(">", "&gt;")}</pre>
                """.trimIndent()
            }
        }
    }
    // Load string resources (JSON) before UI so sharedString() shows translated text
    fun runComposeAfterStrings() {
        GlobalScope.launch {
            console.log("[Web] runComposeAfterStrings: start")
            initSharedStringProvider()
            // Align Moko StringDesc / stringResource() with saved app language before first frame
            // (otherwise web header chips stay English until user toggles language).
            LocaleHelper.updateLocaleAndShortcuts(getLanguage())
            console.log("[Web] initDatabase() ...")
            DatabaseProvider.initDatabase(DatabaseDriverFactory())
            try {
                console.log("[Web] runSchema() ...")
                DatabaseProvider.runSchema()
                console.log("[Web] runSchema() done")
            } catch (e: Throwable) {
                console.log("[Web] runSchema() FAILED: ${e.message}")
                console.error(e)
            }
            // Проверка веднага след схемата (в същия поток)
            try {
                val dao = DatabaseProvider.getEmbeddingsDao()
                val count = withTimeout(3_000L) { dao?.getEmbeddingsCount() ?: -1L }
                console.log("[Web] post-schema check: getEmbeddingsCount()=$count")
            } catch (e: Throwable) {
                console.log("[Web] post-schema check FAILED: ${e.message}")
            }
            runCompose()
        }
    }
    // Skiko WASM (skiko.js) sets org_jetbrains_skiko_node_* globals asynchronously; run UI only when ready
    val onWasmReady = (window.asDynamic()).onWasmReady
    if (onWasmReady != undefined) {
        onWasmReady(::runComposeAfterStrings)
    } else {
        runComposeAfterStrings()
    }
}
